import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CircleNotch, Gauge, PencilSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { Kpi, fmtGb, when } from "./Kpi";
import { RailwaySection } from "./RailwaySection";

const ser = (rows, pick) => rows.map((r) => ({ ts: r.at, v: pick(r) })).filter((x) => x.v != null);

function LimitsDialog({ open, setOpen, limits, onSaved }) {
  const [f, setF] = useState(limits);
  useEffect(() => setF(limits), [limits]);
  const num = (k) => (e) => setF({ ...f, [k]: e.target.value === "" ? (k.endsWith("_pct") ? limits[k] : null) : Number(e.target.value) });
  const save = async () => {
    try { await api.put("/admin/resources/limits", f); toast.success("Allocations saved"); setOpen(false); onSaved(); } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const field = (k, label) => (
    <label className="text-xs text-slate-600 space-y-1"><span>{label}</span><Input type="number" step="any" value={f[k] ?? ""} onChange={num(k)} data-testid={`res-limit-${k}`} /></label>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="res-limits-dialog">
        <DialogHeader><DialogTitle>Emergent plan & allocations (manual)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Emergent has no public API for plan limits. Memory and vCPU limits are read live from the server when available; use these fields for anything else.</p>
        <label className="text-xs text-slate-600 space-y-1 block"><span>Plan / tier name</span><Input value={f.plan ?? ""} onChange={(e) => setF({ ...f, plan: e.target.value })} data-testid="res-limit-plan" /></label>
        <div className="grid grid-cols-2 gap-3">
          {field("ram_gb", "RAM (GB) — used only if the server can't report it")}{field("vcpu", "vCPU — used only if the server can't report it")}{field("storage_gb", "Media storage allowance (GB)")}{field("db_storage_gb", "Database allowance (GB)")}
          {field("warn_pct", "Warn at %")}{field("critical_pct", "Critical at %")}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#327de3] text-white hover:bg-[#1f64c2]" onClick={save} data-testid="res-limits-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmergentSection({ rows, limits, onEdit }) {
  const cur = rows[rows.length - 1] || {};
  const m = cur.media || {};
  const ramAlloc = cur.memory_limit_gb ?? limits.ram_gb;
  const live = "Live · server";
  return (
    <div className="min-w-0" data-testid="res-emergent">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-bold text-slate-800">Emergent Resources <span className="font-normal text-slate-400">· {limits.plan || "plan not entered (unverified: 2nd tier, 4 GB)"}</span></div>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} className="text-[11px] text-[#327de3] font-semibold inline-flex items-center gap-1" data-testid="res-edit-limits"><PencilSimple size={11} /> Edit allocations</button>
          <a href="https://app.emergent.sh" target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-[#327de3] inline-flex items-center gap-1" data-testid="res-emergent-upgrade">Upgrade <ArrowSquareOut size={11} /></a>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-2">
        <Kpi testId="res-em-memory" title="Memory (API server)" used={cur.memory_used_gb} alloc={ramAlloc} fmt={fmtGb} series={ser(rows, (r) => r.memory_used_gb)} limits={limits}
          source={cur.memory_limit_gb ? live : "Live used · manual limit"} note="Memory used by the Strata API container vs. its allocated RAM, measured directly from the container (cgroup). Shows this environment only." />
        <Kpi testId="res-em-cpu" title="CPU (API server)" used={cur.cpu_used_cores} alloc={cur.vcpu_allocated ?? limits.vcpu} fmt={(v) => `${v} vCPU`} series={ser(rows, (r) => r.cpu_used_cores)} limits={limits}
          source={live} note="CPU actually in use, sampled over 1 second, vs. the vCPU allocated to the container." color="#8b5cf6" />
        <Kpi testId="res-em-media" title="Media storage (Object Storage)" used={m.media_total_gb} alloc={limits.storage_gb} fmt={fmtGb} series={ser(rows, (r) => r.media?.media_total_gb)} limits={limits}
          source={limits.storage_gb ? "Strata records · manual limit" : "Strata records · limit unknown"} color="#10b981"
          note={`Photos ${fmtGb(m.photos_gb)} (${m.counts?.photos ?? 0}), videos ${fmtGb(m.videos_gb)} (${m.counts?.videos ?? 0}), other ${fmtGb(m.other_files_gb)}. These files live in Emergent Object Storage, not on Railway or the database. Totals come from the file sizes Strata records (originals; thumbnails and stamped copies aren't counted).`} />
        <Kpi testId="res-em-db" title="Database (MongoDB)" used={cur.db_used_gb} alloc={limits.db_storage_gb} fmt={fmtGb} series={ser(rows, (r) => r.db_used_gb)} limits={limits}
          source={limits.db_storage_gb ? "Live · manual limit" : "Live · limit unknown"} color="#f59e0b" note="Storage plus index size reported by MongoDB. It holds records only; media files are in Object Storage." />
      </div>
    </div>
  );
}

export function ResourcePanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/resources"); setD(data); } catch (e) { toast.error(apiErrorMessage(e, "Could not load resources")); } }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setBusy(true);
    try { await api.post("/admin/resources/refresh"); toast.success("Metrics refreshed"); await load(); } catch (e) { toast.error(apiErrorMessage(e, "Refresh failed")); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 min-h-[25vh]" data-testid="resource-panel">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Gauge size={16} className="text-[#327de3]" /> Infrastructure resources
          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid="resource-env">{d?.env || "…"}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span data-testid="resource-last-updated">Last updated: {when(d?.last_collected_at)}</span>
          <span className="text-slate-300">·</span><span>Auto: daily 6:00 AM ET · thresholds {d?.limits?.warn_pct ?? 70}% / {d?.limits?.critical_pct ?? 90}% (guidance)</span>
          <Button size="sm" onClick={refresh} disabled={busy} className="h-7 bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="resource-refresh-btn">
            {busy ? <CircleNotch size={12} className="mr-1 animate-spin" /> : <ArrowsClockwise size={12} className="mr-1" />} Refresh Now
          </Button>
        </div>
      </div>
      {!d ? <div className="h-32 flex items-center justify-center text-xs text-slate-400"><CircleNotch className="animate-spin mr-2" /> Loading…</div> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <RailwaySection d={d} limits={d.limits} reload={load} />
          <EmergentSection rows={d.emergent} limits={d.limits} onEdit={() => setEdit(true)} />
        </div>
      )}
      {d && <LimitsDialog open={edit} setOpen={setEdit} limits={d.limits} onSaved={load} />}
    </section>
  );
}
